import { CustomCode } from '@/components/ManageDynamicPage/SettingsModal/CustomCode.tsx';
import { Design } from '@/components/ManageDynamicPage/SettingsModal/Design.tsx';
import { General } from '@/components/ManageDynamicPage/SettingsModal/General.tsx';
import { Integrations } from '@/components/ManageDynamicPage/SettingsModal/Integrations.tsx';
import { QuizOptions } from '@/components/ManageDynamicPage/SettingsModal/QuizOptions.tsx';
import { SearchAndSocial } from '@/components/ManageDynamicPage/SettingsModal/SearchAndSocial.tsx';
import { Tracking } from '@/components/ManageDynamicPage/SettingsModal/Tracking.tsx';
import { DynamicPage } from '@superfunnel/types';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@superfunnel/ui-core/v2/Dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@superfunnel/ui-core/v2/Tabs';
import {
  Code,
  ControlSlider,
  DesignNib,
  Link,
  NetworkReverse,
  Puzzle,
  Search,
  SelectWindow,
  Xmark,
} from 'iconoir-react';
import { ReactNode, useMemo } from 'react';
import { AdsAccountsConnections } from './SettingsModal/AdsAccountsConnections';
import { DynamicPageSettingsProvider } from './SettingsModal/DynamicPageSettingsContext';

export type DynamicPageSettingsTabId =
  | 'general'
  | 'design'
  | 'ads-accounts'
  | 'search-and-social'
  | 'custom-code'
  | 'tracking'
  | 'integrations'
  | 'quiz-options';

export type HiddenGeneralField = 'goal' | 'logoLink';

export interface DeleteConfig {
  /** Custom text for the delete button */
  buttonText: string;
  /** Custom title for the delete confirmation modal */
  confirmTitle: string;
  /** Custom delete handler. If provided, will be used instead of default deleteDynamicPage */
  onDelete: () => Promise<void>;
}

export interface QuizOptionsConfig {
  /** Form name for the quiz */
  formName: string;
  /** Auto-advance delay in milliseconds */
  autoAdvanceDelay: number;
  /** Callback to save quiz options on modal Save */
  onSave: (options: {
    formName: string;
    autoAdvanceDelay: number;
  }) => Promise<void>;
}

interface IDynamicPageSettingsModal {
  open: boolean;
  setOpen: (open: boolean) => void;
  dynamicPageId: string | undefined;
  dynamicPage: DynamicPage | undefined;
  defaultTab?: DynamicPageSettingsTabId;
  visibleTabs?: DynamicPageSettingsTabId[];
  /** Optional custom dialog title */
  dialogTitle?: string;
  /** Fields to hide in the General tab */
  hideGeneralFields?: HiddenGeneralField[];
  /** Custom delete configuration */
  deleteConfig?: DeleteConfig;
  /** Quiz options configuration (enables quiz-options tab content) */
  quizOptions?: QuizOptionsConfig;
  /** Optional custom update function for page data */
  onUpdatePage?: (data: any, mergeFields?: string[]) => Promise<void>;
  /** Optional custom update function for metadata */
  onUpdateMetadata?: (metadata: {
    path?: string;
    alternatePathes?: string[];
  }) => Promise<void>;
}

const ALL_TABS: {
  id: DynamicPageSettingsTabId;
  icon: ReactNode;
  name: string;
}[] = [
  {
    id: 'general',
    icon: <SelectWindow className={'size-5'} />,
    name: 'General',
  },
  {
    id: 'design',
    icon: <DesignNib className={'size-5'} />,
    name: 'Design',
  },
  {
    id: 'ads-accounts',
    icon: <NetworkReverse className={'size-5'} />,
    name: 'Ads Accounts',
  },
  {
    id: 'search-and-social',
    icon: <Search className={'text-base-500 size-5'} />,
    name: 'Search & Social',
  },
  {
    id: 'custom-code',
    icon: <Code className={'text-base-500 size-5'} />,
    name: 'Custom Code',
  },
  {
    id: 'tracking',
    icon: <Link className={'text-base-500 size-5'} />,
    name: 'Tracking',
  },
  {
    id: 'integrations',
    icon: <Puzzle className={'text-base-500 size-5'} />,
    name: 'Integrations',
  },
  {
    id: 'quiz-options',
    icon: <ControlSlider className={'text-base-500 size-5'} />,
    name: 'Quiz Options',
  },
];

export const DynamicPageSettingsModal = ({
  open,
  setOpen,
  dynamicPageId,
  dynamicPage,
  defaultTab = 'general',
  visibleTabs,
  dialogTitle = 'Dynamic Page Settings',
  hideGeneralFields,
  deleteConfig,
  quizOptions,
  onUpdatePage,
  onUpdateMetadata,
}: IDynamicPageSettingsModal) => {
  const filteredTabs = useMemo(() => {
    if (!visibleTabs) return ALL_TABS;
    return ALL_TABS.filter((tab) => visibleTabs.includes(tab.id));
  }, [visibleTabs]);

  const effectiveDefaultTab = useMemo(() => {
    if (filteredTabs.some((tab) => tab.id === defaultTab)) {
      return defaultTab;
    }
    return filteredTabs[0]?.id ?? 'general';
  }, [filteredTabs, defaultTab]);

  const subTabs = filteredTabs;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DynamicPageSettingsProvider
        dynamicPageId={dynamicPageId}
        dynamicPage={dynamicPage}
        onUpdatePage={onUpdatePage}
        onUpdateMetadata={onUpdateMetadata}
        hideGeneralFields={hideGeneralFields}
        deleteConfig={deleteConfig}
        quizOptions={quizOptions}
      >
        <DialogContent
          className="flex h-192 max-h-screen flex-col overflow-hidden p-6 sm:max-h-11/12 sm:max-w-264"
          showCloseButton={false}
          aria-describedby={''}
        >
          <DialogHeader className={''}>
            <DialogTitle className={'text-lg/[1] -tracking-[0.01em]'}>
              {dialogTitle}
            </DialogTitle>
            <DialogClose className={'absolute top-6 right-6.5'}>
              <Xmark className={'size-5'} />
            </DialogClose>
          </DialogHeader>
          <Tabs
            defaultValue={effectiveDefaultTab}
            orientation={'vertical'}
            className="relative grid h-full w-full grow grid-cols-1 gap-6 overflow-hidden bg-transparent"
          >
            <TabsList
              className={
                'top-0 left-0 z-10 h-full w-full flex-wrap items-start justify-start gap-0.5 bg-transparent p-0 md:!absolute md:w-40 md:flex-col'
              }
            >
              {subTabs.map((tab) => (
                <TabsTrigger
                  value={tab.id}
                  key={tab.id}
                  className={
                    'data-[state=active]:bg-base-100 hover:bg-base-50 h-auto flex-none justify-start gap-2.5 p-2 text-sm data-[state=active]:shadow-none md:w-full'
                  }
                >
                  {tab.icon} {tab.name}
                </TabsTrigger>
              ))}
            </TabsList>
            <TabsContent value="general" className={'h-full overflow-hidden'}>
              <General />
            </TabsContent>
            <TabsContent value="design" className={'h-full overflow-hidden'}>
              <Design />
            </TabsContent>
            <TabsContent
              value="ads-accounts"
              className={'h-full overflow-hidden'}
            >
              <AdsAccountsConnections />
            </TabsContent>
            <TabsContent
              value="search-and-social"
              className={'h-full overflow-hidden'}
            >
              <SearchAndSocial />
            </TabsContent>
            <TabsContent
              value="custom-code"
              className={'h-full overflow-hidden'}
            >
              <CustomCode />
            </TabsContent>
            <TabsContent value="tracking" className={'h-full overflow-hidden'}>
              <Tracking />
            </TabsContent>
            <TabsContent
              value="integrations"
              className={'h-full overflow-hidden'}
            >
              <Integrations />
            </TabsContent>
            <TabsContent
              value="quiz-options"
              className={'h-full overflow-hidden'}
            >
              <QuizOptions />
            </TabsContent>
          </Tabs>
        </DialogContent>
      </DynamicPageSettingsProvider>
    </Dialog>
  );
};
